import { fetch } from '@microsoft/portal-app/lib/fetch';
import { isTenantEmailSettings, isEnduserEmailContacts } from './models';
import { topicIds } from '../constants';
import {
  actionContactPreferenceUserFetch,
  actionContactPreferenceUserFetchSuccess,
  actionContactPreferenceUserFetchError,
} from '../actions';
import { selectContactPreferenceUserFetchDisabled } from '../selectors';
import { createThunk } from '../../redux';
import { auth } from '../../auth';
import { settingsManager } from '../../shared/settings';

export const effectContactPreferenceUserFetch = createThunk(
  async (dispatch, getState) => {
    if (selectContactPreferenceUserFetchDisabled(getState())) {
      return;
    }

    const audience = settingsManager.getRequired('cpmAudience');
    const baseUrl = settingsManager.getRequired('cpmBaseUrl');

    dispatch(actionContactPreferenceUserFetch());

    const token = await auth.acquireToken({ scopes: [audience] });
    const user = auth.user;
    const objectId = user?.objectId ?? '';
    const tenantId = user?.tenantId ?? '';

    if (!(await getTenantEnabled(baseUrl, token, tenantId))) {
      dispatch(actionContactPreferenceUserFetchSuccess({ productsOptIn: false, enabled: false }));
      return;
    }

    const response = await fetch(`${baseUrl}/api/EnduserEmailContacts`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'x-ms-filter-object-id': objectId,
        'x-ms-filter-tenant-id': tenantId,
      },
    });

    if (!response.ok) {
      throw Error(`error response from GET ${response.url} (status: ${response.status})`);
    }

    const data = await response.json();

    if (!isEnduserEmailContacts(data)) {
      throw Error(`invalid response from GET ${response.url}`);
    }

    const state = data.topicSettings.find((topic) => topic.topicId === topicIds.products)?.state ?? 'optOut';
    const optIn = state !== 'optOut';

    dispatch(actionContactPreferenceUserFetchSuccess({ enabled: true, productsOptIn: optIn }));
  },
  (error, dispatch) => {
    dispatch(actionContactPreferenceUserFetchError(error));
  },
);

async function getTenantEnabled(baseUrl: string, token: string, tenantId: string): Promise<boolean> {
  const response = await fetch(`${baseUrl}/api/TenantEmailSettings`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'x-ms-filter-tenant-id': tenantId,
    },
  });

  if (response.status === 400) {
    // The user hasn't saved any contact preference yet. Not sure why this
    // is a 400 and not a 404. A+ API design.
    return false;
  } else if (!response.ok) {
    throw Error(`error response from GET ${response.url} (status: ${response.status})`);
  }

  const data = await response.json();

  if (!isTenantEmailSettings(data)) {
    throw Error(`invalid response from GET ${response.url}`);
  }

  const state = data.topicSettings.find((topic) => topic.topicId === topicIds.products)?.state ?? 'optOut';

  return state !== 'optOut';
}
